/*
 * Shopify's storefront search has no spelling correction. It matches on vector
 * similarity, so a misspelling either happens to land near the real word or it
 * returns next to nothing: "kostuum" finds 839 products, "costuum" finds zero or one,
 * and "vostuum" and "lostuum" are just as dead. There is no setting for this — the
 * correction step does not exist — so the theme adds one.
 *
 * This offers the correction rather than applying it: the shopper gets a "did you
 * mean kostuum?" link and decides. That keeps a rare-but-real search term from being
 * silently rewritten into something else.
 *
 * Words are looked up in the shop's own vocabulary (assets/search-vocabulary.json,
 * regenerated by scripts/build-search-vocabulary.mjs), which is only fetched for
 * searches that went badly.
 */
class SearchSpellCorrect extends HTMLElement {
  // Below five characters an edit distance of 1 covers too many unrelated words
  // ("paw" would reach "pak", "raw", "paws"), so short words are left alone.
  static MIN_LENGTH = 5;

  // Longer words carry more typos without becoming ambiguous.
  static LONG_WORD_LENGTH = 8;

  /*
   * A search is worth second-guessing well before it returns nothing at all.
   * Shopify's results are not deterministic: "costuum" returns 0 on some requests
   * and 1 on others, where the correct spelling returns 839. Triggering on an empty
   * page only would make the suggestion appear at random.
   */
  static MAX_RESULTS = 20;

  connectedCallback() {
    const term = this.dataset.term;
    const count = Number(this.dataset.resultsCount);

    if (!term || !Number.isFinite(count) || count > SearchSpellCorrect.MAX_RESULTS) return;

    this.suggest(term);
  }

  async suggest(term) {
    let vocabulary;
    try {
      const response = await fetch(this.dataset.vocabularyUrl);
      if (!response.ok) return;
      vocabulary = await response.json();
    } catch (error) {
      return;
    }

    const known = new Set(vocabulary);
    const words = term.toLowerCase().split(/\s+/).filter(Boolean);
    let changed = false;

    const suggested = words.map((word) => {
      const nearest = this.nearest(word, vocabulary, known);
      if (nearest) changed = true;
      return nearest || word;
    });

    if (changed) this.render(suggested.join(' '));
  }

  render(suggestion) {
    const template = this.dataset.suggestionHtml;
    if (!template) return;

    const url = new URL(this.dataset.searchUrl, window.location.origin);
    url.searchParams.set('q', suggestion);

    const paragraph = document.createElement('p');
    paragraph.className = 'search-spelling-suggestion';
    // Both values are ours: the suggestion comes from the vocabulary, which holds
    // nothing but letters, and the URL is built by URL/searchParams.
    paragraph.innerHTML = template
      .replaceAll('%{link}', url.pathname + url.search)
      .replaceAll('%{suggestion}', suggestion);
    this.replaceWith(paragraph);
  }

  nearest(word, vocabulary, known) {
    if (word.length < SearchSpellCorrect.MIN_LENGTH || known.has(word)) return null;

    const limit = word.length < SearchSpellCorrect.LONG_WORD_LENGTH ? 1 : 2;
    let best = null;
    let bestDistance = limit + 1;

    for (const candidate of vocabulary) {
      const distance = this.distance(word, candidate, bestDistance - 1);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = candidate;
        if (distance === 1) break;
      }
    }

    return bestDistance <= limit ? best : null;
  }

  // Levenshtein distance, abandoned as soon as it cannot beat `cap`.
  distance(a, b, cap) {
    if (cap < 0 || Math.abs(a.length - b.length) > cap) return cap + 1;

    let previous = Array.from({ length: b.length + 1 }, (_, i) => i);

    for (let i = 1; i <= a.length; i++) {
      const current = [i];
      let rowMinimum = i;

      for (let j = 1; j <= b.length; j++) {
        current[j] = Math.min(
          previous[j] + 1,
          current[j - 1] + 1,
          previous[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
        rowMinimum = Math.min(rowMinimum, current[j]);
      }

      if (rowMinimum > cap) return cap + 1;
      previous = current;
    }

    return previous[b.length];
  }
}

customElements.define('search-spell-correct', SearchSpellCorrect);
